/**
 * AgentDetail Component
 * Panel de detalles del agente con métricas
 */

import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Chip,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  PowerSettingsNew as RebootIcon,
  Power as ShutdownIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  SystemUpdate as UpdateIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { fromNowEs } from '@/utils/dateUtils';
import { compareVersions } from '@/utils/version';
import {
  useAgentMetrics,
  useAgentPolicy,
  useDeleteAgent,
  useUpdateAgentPolicy,
  useUpdateAgentHostname,
  useSendCommand,
} from '@/hooks/useAgents';
import { useAgentsStore } from '@/store/agentsStore';
import type { Agent } from '@/types/agent';
import { AgentServicePanel } from './AgentServicePanel';
import { UpdateProgressCell } from './UpdateProgressCell';
import { isActiveUpdateStatus } from '@/lib/updates/updateDisplay';

interface UpdateManifestLite {
  version: string;
  assets?: Array<{ os: string; arch: string }>;
}

interface AgentDetailProps {
  agent: Agent | null;
  open: boolean;
  onClose: () => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatPercent = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export const AgentDetail: React.FC<AgentDetailProps> = ({ agent, open, onClose }) => {
  const [editingHostname, setEditingHostname] = useState(false);
  const [newHostname, setNewHostname] = useState('');

  const { data: metrics, isLoading: metricsLoading } = useAgentMetrics(agent?.agentId || null);
  const { data: policy } = useAgentPolicy(agent?.agentId || null);
  const deleteAgent = useDeleteAgent();
  const updatePolicy = useUpdateAgentPolicy(agent?.agentId || '');
  const updateHostname = useUpdateAgentHostname(agent?.agentId || '');
  const sendCommand = useSendCommand(agent?.agentId || '');

  // Manifiesto portable (CalVer): el .exe del servicio portable se auto-actualiza.
  const { data: manifest } = useQuery<UpdateManifestLite>({
    queryKey: ['agent-update-manifest', 'portable'],
    queryFn: async () => {
      const res = await apiClient.get<UpdateManifestLite>(
        '/agent-update?channel=stable&portable=true'
      );
      return res.data;
    },
    // El manifiesto se arma en vivo desde GitHub: tolerar fallas puntuales.
    retry: 2,
    retryDelay: 1500,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const updateState = useAgentsStore((s) => (agent ? s.updateStates[agent.agentId] : undefined));

  const handleStartEdit = () => {
    setNewHostname(agent?.hostname || agent?.agentId || '');
    setEditingHostname(true);
  };

  const handleSaveHostname = () => {
    if (newHostname && agent) {
      updateHostname.mutate(newHostname, {
        onSuccess: () => setEditingHostname(false),
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingHostname(false);
    setNewHostname('');
  };

  const handleDelete = async () => {
    if (!agent) return;
    if (confirm(`¿Querés eliminar el agente ${agent.hostname || agent.agentId}?`)) {
      deleteAgent.mutate(agent.agentId, {
        onSuccess: onClose,
      });
    }
  };

  const handleReboot = () => {
    if (confirm('¿Querés reiniciar el agente?')) {
      sendCommand.mutate({ command: 'reboot' });
    }
  };

  const handleShutdown = () => {
    if (confirm('¿Querés apagar el agente?')) {
      sendCommand.mutate({ command: 'shutdown' });
    }
  };

  const handlePolicyChange = (allowDangerous: boolean) => {
    updatePolicy.mutate({ allowDangerous });
  };

  const handleUpdate = () => {
    sendCommand.mutate({ command: 'check_update' });
  };

  if (!agent) return null;

  const latestVersion = manifest?.version;
  const isUpdating = isActiveUpdateStatus(updateState?.status);
  const outdated =
    !!latestVersion && !!agent.version && compareVersions(agent.version, latestVersion) < 0;
  // Si no pudimos traer el manifiesto, no podemos comparar: igual permitimos
  // disparar la actualización (el agente verifica la versión server-side).
  const versionUnknown = !latestVersion && !!agent.version;
  const canForceUpdate = agent.connected && (outdated || versionUnknown);

  const displayName = agent.hostname || agent.agentId;
  const osInfo = agent.os ? `${agent.os}${agent.arch ? ` ${agent.arch}` : ''}` : '—';
  const lastSeen = agent.lastSeen ? fromNowEs(agent.lastSeen) : '—';

  const cpuPercent = metrics?.cpuPercent || 0;
  const memRatio = metrics?.memTotalBytes
    ? (metrics.memUsedBytes / metrics.memTotalBytes) * 100
    : 0;
  const diskRatio = metrics?.diskTotalBytes
    ? (metrics.diskUsedBytes / metrics.diskTotalBytes) * 100
    : 0;
  const disks = metrics?.disks?.length ? metrics.disks : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { borderRadius: '0px' } } }}
    >
      <Box sx={{ width: 400, p: 3, maxWidth: '90vw', paddingTop: 15 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Detalles del Agente</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Hostname editable */}
        <Box mb={3}>
          {editingHostname ? (
            <Box display="flex" gap={1}>
              <TextField
                size="small"
                fullWidth
                value={newHostname}
                onChange={(e) => setNewHostname(e.target.value)}
                autoFocus
              />
              <IconButton size="small" onClick={handleSaveHostname}>
                <CheckIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CancelIcon />
              </IconButton>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h5" sx={{ flexGrow: 1 }}>
                {displayName}
              </Typography>
              <IconButton size="small" onClick={handleStartEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {osInfo}
          </Typography>
        </Box>

        {/* Estado */}
        <List dense>
          <ListItem>
            <ListItemText
              primary="Estado"
              secondary={
                <span>
                  <Chip
                    label={agent.connected ? 'Conectado' : 'Offline'}
                    size="small"
                    color={agent.connected ? 'success' : 'default'}
                    sx={{ mt: 0.5 }}
                  />
                </span>
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItem>
          <ListItem>
            <ListItemText primary="Último visto" secondary={lastSeen} />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Versión"
              secondary={
                <Box component="span" display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <span>v{agent.version || '—'}</span>
                  {outdated && (
                    <Chip label={`Actualizable → v${latestVersion}`} size="small" color="warning" />
                  )}
                  {!outdated && latestVersion && agent.version && (
                    <Chip label="Al día" size="small" color="success" variant="outlined" />
                  )}
                </Box>
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItem>
          <ListItem>
            <ListItemText primary="ID" secondary={agent.agentId} />
          </ListItem>
        </List>

        {/* Actualización: botón visible si está desactualizado o no pudimos comparar */}
        {(canForceUpdate || isUpdating || updateState) && (
          <Box mb={1}>
            {isUpdating ||
            (updateState &&
              updateState.status !== 'complete' &&
              updateState.status !== 'already_up_to_date') ? (
              <UpdateProgressCell
                state={updateState ?? null}
                agent={agent}
                latestVersion={manifest?.version}
                onRetry={handleUpdate}
                retryDisabled={!agent.connected || sendCommand.isPending}
                compact={false}
              />
            ) : updateState?.status === 'complete' ||
              updateState?.status === 'already_up_to_date' ? (
              <Chip label="Actualización completada" size="small" color="success" />
            ) : canForceUpdate ? (
              <Button
                variant="contained"
                color="warning"
                startIcon={<UpdateIcon />}
                onClick={handleUpdate}
                disabled={!agent.connected || sendCommand.isPending}
                fullWidth
              >
                {outdated ? `Actualizar a v${latestVersion}` : 'Buscar actualización'}
              </Button>
            ) : null}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Métricas */}
        <Typography variant="subtitle2" gutterBottom>
          Métricas
        </Typography>

        {metricsLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : metrics ? (
          <List dense>
            <ListItem>
              <ListItemText
                primary="CPU"
                secondary={
                  <Box component="div">
                    <LinearProgress variant="determinate" value={cpuPercent} sx={{ mb: 0.5 }} />
                    <Typography variant="caption">{formatPercent(cpuPercent)}</Typography>
                  </Box>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="RAM"
                secondary={
                  <Box component="div">
                    <LinearProgress variant="determinate" value={memRatio} sx={{ mb: 0.5 }} />
                    <Typography variant="caption">
                      {formatBytes(metrics.memUsedBytes)} / {formatBytes(metrics.memTotalBytes)}
                    </Typography>
                  </Box>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
            {disks ? (
              disks.map((d) => (
                <ListItem key={d.path}>
                  <ListItemText
                    primary={`Disco ${d.path}`}
                    secondary={
                      <Box component="div">
                        <LinearProgress
                          variant="determinate"
                          value={d.usedPercent}
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption">
                          {formatBytes(d.freeBytes)} libre · {formatBytes(d.usedBytes)} usado ·{' '}
                          {Math.round(d.usedPercent)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Total: {formatBytes(d.totalBytes)}
                          {d.fstype ? ` · ${d.fstype}` : ''}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary="Disco"
                  secondary={
                    <Box component="div">
                      <LinearProgress variant="determinate" value={diskRatio} sx={{ mb: 0.5 }} />
                      <Typography variant="caption">
                        {formatBytes(metrics.diskUsedBytes)} / {formatBytes(metrics.diskTotalBytes)}
                      </Typography>
                    </Box>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            )}
            <ListItem>
              <ListItemText primary="Procesos" secondary={metrics.processes?.length || '—'} />
            </ListItem>
            {metrics.activeApp && (
              <ListItem>
                <ListItemText
                  primary="App activa"
                  secondary={
                    metrics.activeApp.available
                      ? metrics.activeApp.name || '—'
                      : `No disponible (${metrics.activeApp.reason || '—'})`
                  }
                />
              </ListItem>
            )}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No hay métricas disponibles
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Policy */}
        <Typography variant="subtitle2" gutterBottom>
          Políticas
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={policy?.allowDangerous || false}
              onChange={(e) => handlePolicyChange(e.target.checked)}
            />
          }
          label="Permitir comandos peligrosos"
        />

        {/* Servicio Windows */}
        <AgentServicePanel agent={agent} />

        <Divider sx={{ my: 2 }} />

        {/* Acciones */}
        <Typography variant="subtitle2" gutterBottom>
          Acciones
        </Typography>
        <Stack spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RebootIcon />}
            onClick={handleReboot}
            disabled={!agent.connected}
            fullWidth
          >
            Reiniciar
          </Button>
          <Button
            variant="outlined"
            startIcon={<ShutdownIcon />}
            onClick={handleShutdown}
            disabled={!agent.connected}
            fullWidth
          >
            Apagar
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            fullWidth
          >
            Eliminar agente
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
